import clsx from 'clsx'
import React, { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'
import { v4 as uuidv4 } from 'uuid'

import s from './styles.module.scss'

import { BillingColumns } from '../../BillingColumns'
import { DescriptionMode } from '../../DescriptionMode'
import { ComputedPrice, TravelTypeEnum } from '../../../../../../../lib/graphql'
import { useTravelTypeContext } from '../../../../../../contexts'
import LinePrices from '../../../pricing-item-details/LinePrices'

const LineTotal = ({
  label,
  price,
  priceWithDiscount,
  adults,
  descriptionMode,
  columns,
  showAdjustments = false,
  isExtraTotal = false,
  className,
}: {
  label?: string | ReactElement
  price?: ComputedPrice
  priceWithDiscount?: ComputedPrice
  adults?: number
  descriptionMode?: DescriptionMode
  columns: BillingColumns[]
  showAdjustments?: boolean
  isExtraTotal?: boolean
  className?: string
}) => {
  const { travelType } = useTravelTypeContext()

  const getPrice = ({
    price,
    adults,
  }: {
    price?: ComputedPrice
    adults?: number
  }) => {
    if (!price) {
      return undefined
    }

    if (adults) {
      return {
        priceWithoutVat: price.priceWithoutVat / adults,
        priceWithVat: price.priceWithVat / adults,
        vat: price.vat / adults,
        vatRate: price.vatRate,
      }
    } else {
      return price
    }
  }

  return (
    <LinePrices
      clientPricingItem={{
        _id: uuidv4(),
        objectData: {
          label:
            typeof label === 'string' ? (
              isExtraTotal ? (
                (label ?? '')
              ) : adults ? (
                travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT ? (
                  <FormattedMessage
                    id='ui.LineTotal.labelTotalEvent'
                    defaultMessage='Total {label} par participant'
                    values={{ label }}
                  />
                ) : (
                  <FormattedMessage
                    id='ui.LineTotal.labelTotalStay'
                    defaultMessage='Total {label} par voyageur'
                    values={{ label }}
                  />
                )
              ) : (
                <FormattedMessage
                  id='ui.LineTotal.labelTotal'
                  defaultMessage='Total {label}'
                  values={{ label }}
                />
              )
            ) : (
              (label ?? '')
            ),
          description: '',
        },
        unitPrice: null,
        totalPrice: {
          basePrice: getPrice({ price, adults }),
          priceWithDiscount: getPrice({ price: priceWithDiscount, adults }),
        },
        priceOption: {
          quantity: 1,
          fees: undefined,
        },
      }}
      columns={columns}
      total={{
        isTotal: true,
        isTotalPerAdult: !!adults,
      }}
      displayMode={descriptionMode}
      showAdjustments={showAdjustments}
      className={className}
    />
  )
}

const LinesTotal = ({
  label = '',
  price,
  priceWithDiscount,
  adults,
  descriptionMode,
  columns,
  showAdjustments = false,
  isExtraTotal = false,
  containerClassName,
  className,
  clientProposalType,
}: {
  label?: string | ReactElement
  price?: ComputedPrice | null
  priceWithDiscount?: ComputedPrice | null
  adults?: number
  descriptionMode?: DescriptionMode
  columns: BillingColumns[]
  showAdjustments?: boolean
  isExtraTotal?: boolean
  containerClassName?: string
  className?: string
  clientProposalType?: TravelTypeEnum
}) => {
  return (
    <div className={clsx(s.blockTotal, containerClassName)}>
      <LineTotal
        label={label}
        price={price ?? undefined}
        priceWithDiscount={priceWithDiscount ?? undefined}
        descriptionMode={descriptionMode}
        columns={columns}
        showAdjustments={showAdjustments}
        isExtraTotal={isExtraTotal}
        className={className}
      />

      {adults && clientProposalType && (
        <LineTotal
          label={label}
          price={price ?? undefined}
          priceWithDiscount={priceWithDiscount ?? undefined}
          descriptionMode={descriptionMode}
          adults={adults}
          columns={columns}
          showAdjustments={showAdjustments}
          isExtraTotal={isExtraTotal}
          className={className}
        />
      )}
    </div>
  )
}

export default LinesTotal
